import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import AdminLayout from "@/components/admin/AdminLayout";
import axios from "axios";
import { Plus, Edit2, Trash2, X, Save, Loader2 } from "lucide-react";
import { ADMIN } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminMetrics() {
  const { authHeader } = useAuth();
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ label: "", value: "", display_order: 0, active: true });
  const [saving, setSaving] = useState(false);

  const fetchMetrics = () => {
    setLoading(true);
    axios.get(`${API}/metrics`, { headers: authHeader() })
      .then(r => setMetrics(r.data.metrics || []))
      .finally(() => setLoading(false));
  };
  useEffect(fetchMetrics, []);

  const openCreate = () => { setForm({ label: "", value: "", display_order: metrics.length + 1, active: true }); setEditing(null); setShowModal(true); };
  const openEdit = (m) => { setForm({ label: m.label, value: m.value, display_order: m.display_order || 0, active: m.active !== false }); setEditing(m.id); setShowModal(true); };

  const handleSave = async () => {
    if (!form.label || !form.value) return;
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/metrics/${editing}`, form, { headers: authHeader() });
      } else {
        await axios.post(`${API}/metrics`, form, { headers: authHeader() });
      }
      setShowModal(false);
      fetchMetrics();
    } catch {}
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this metric?")) return;
    await axios.delete(`${API}/metrics/${id}`, { headers: authHeader() }).catch(() => {});
    fetchMetrics();
  };

  return (
    <AdminLayout title="Site Metrics">
      <div data-testid={ADMIN.metricsTable} className="space-y-4">
        <div className="flex justify-between items-center">
          <p className="text-sm text-slate-500">These metrics are displayed on the homepage. Edit values to update the public site.</p>
          <button onClick={openCreate}
            className="flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-blue-700 transition-all">
            <Plus size={16} /> Add Metric
          </button>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-medium text-slate-500">Label</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-slate-500">Value</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-slate-500">Order</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-slate-500">Visible</th>
                <th className="text-right px-6 py-3 text-xs font-medium text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {loading ? (
                <tr><td colSpan={5} className="px-6 py-12 text-center text-slate-400">Loading...</td></tr>
              ) : metrics.map(m => (
                <tr key={m.id} className="hover:bg-slate-50/50">
                  <td className="px-6 py-4 font-medium text-slate-900">{m.label}</td>
                  <td className="px-6 py-4 text-blue-600 font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>{m.value}</td>
                  <td className="px-6 py-4 text-slate-500">{m.display_order}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${m.active !== false ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                      {m.active !== false ? "Visible" : "Hidden"}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => openEdit(m)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                        <Edit2 size={14} />
                      </button>
                      <button onClick={() => handleDelete(m.id)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm">
            <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100">
              <h3 className="font-semibold text-slate-900">{editing ? "Edit Metric" : "Add Metric"}</h3>
              <button onClick={() => setShowModal(false)} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-50">
                <X size={18} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Label *</label>
                <input value={form.label} onChange={e => setForm({ ...form, label: e.target.value })}
                  placeholder="e.g., Qualified Clinics"
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Value *</label>
                <input value={form.value} onChange={e => setForm({ ...form, value: e.target.value })}
                  placeholder="e.g., 48 or < 24 hrs"
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Display Order</label>
                <input type="number" value={form.display_order} onChange={e => setForm({ ...form, display_order: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.active !== false}
                  onChange={e => setForm({ ...form, active: e.target.checked })}
                  className="rounded border-slate-300 text-blue-600" />
                <span className="text-sm text-slate-700">Visible on homepage</span>
              </label>
            </div>
            <div className="px-6 py-4 border-t border-slate-100 flex gap-3">
              <button onClick={() => setShowModal(false)} className="flex-1 py-2.5 text-sm border border-slate-200 rounded-full text-slate-600 hover:bg-slate-50">Cancel</button>
              <button onClick={handleSave} disabled={saving || !form.label || !form.value}
                className="flex-1 flex items-center justify-center gap-2 bg-blue-600 text-white py-2.5 rounded-full text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-all">
                {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
